'use client';

import {
  SquishEngine,
  type SquishExpression,
  type SquishLayout,
} from '@usespaceui/squishmoji';
import { useEffect, useId, useRef } from 'react';
import {
  ACTIVITY_SHAPE_MORPH_MS,
  ACTIVITY_SHAPE_SEQUENCE,
} from '@/components/chat/agent-activity-sequence';
import {
  getListeningFrame,
  getVoiceColor,
  mixVoiceColor,
  voiceColorCss,
  voiceGradientColor,
  type VoiceAvatarMode,
} from './voice-companion-colors';

const BODY_SCALE = 13 / 112;
const SQUARE_SCALE = 13 / 88.643;
const EXPRESSIONS: readonly SquishExpression[] = [
  'attentive',
  'curious',
  'happy',
  'amazed',
  'excited',
  'proud',
];
const STILL_LAYOUTS = {
  listening: new SquishEngine('IMAI', {
    shape: 'cloud',
    expression: 'curious',
  }).layout(0),
  speaking: new SquishEngine('IMAI', {
    shape: 'circle',
    expression: 'excited',
  }).layout(0),
  idle: new SquishEngine('IMAI', {
    shape: 'square',
    expression: 'attentive',
  }).layout(0),
};

function movingEyes(
  eyes: SquishLayout['eyes'],
  time: number
): SquishLayout['eyes'] {
  const phase = (time + 1.35) % 4.7;
  const lid = phase > 0.18 ? 1 : Math.max(0.035, Math.abs(phase / 0.09 - 1));
  return eyes.map((eye) => {
    const matrix: typeof eye.matrix = [...eye.matrix];
    matrix[3] *= lid;
    matrix[4] += Math.sin(time * 0.64 + 0.8) * 12.4;
    matrix[5] += Math.sin(time * 0.43 + 2.2) * 9.6;
    return { ...eye, matrix };
  });
}

export function VoiceCompanionFace({
  mode,
  running,
}: {
  mode: VoiceAvatarMode;
  running: boolean;
}) {
  const gradientId = useId();
  const pathRef = useRef<SVGPathElement>(null);
  const groupRef = useRef<SVGGElement>(null);
  const eyesRef = useRef<SVGGElement>(null);
  const gradientRef = useRef<SVGLinearGradientElement>(null);
  const initialColor = getVoiceColor(mode, 0);
  const currentColor = useRef(initialColor);
  const previousMode = useRef<VoiceAvatarMode | null>(null);
  const elapsedTime = useRef(0);
  const animationState = useRef<{
    engine: SquishEngine;
    morphAt: number;
  } | null>(null);
  const still = STILL_LAYOUTS[mode];
  const scale = mode === 'idle' ? SQUARE_SCALE : BODY_SCALE;

  useEffect(() => {
    const path = pathRef.current;
    const group = groupRef.current;
    const eyes = eyesRef.current;
    if (!path || !group || !eyes) return;
    const host = path.closest<HTMLElement>(
      '[data-slot="voice-companion-face"]'
    );
    const changed = previousMode.current !== mode;
    const transitionFrom =
      previousMode.current && changed ? currentColor.current : null;
    previousMode.current = mode;
    if (changed) {
      elapsedTime.current = 0;
      animationState.current = null;
    }

    const paint = (layout: SquishLayout, timeMs: number, animate: boolean) => {
      let color = getVoiceColor(mode, timeMs);
      if (animate && transitionFrom && timeMs < 300) {
        color = mixVoiceColor(
          transitionFrom,
          color,
          1 - (1 - timeMs / 300) ** 4
        );
      }
      currentColor.current = color;
      if (host) host.style.color = voiceColorCss(color);
      const offsets = [-1, 0, 1] as const;
      offsets.forEach((offset, index) =>
        gradientRef.current?.children[index]?.setAttribute(
          'stop-color',
          voiceColorCss(voiceGradientColor(color, offset))
        )
      );
      path.setAttribute('d', layout.body);
      eyes.setAttribute('fill', color[0] < 0.58 ? '#fff' : '#17171a');
      layout.eyes.forEach((eye, index) => {
        const rect = eyes.children[index];
        if (!rect) return;
        rect.setAttribute('x', String(-eye.width / 2));
        rect.setAttribute('y', String(-eye.height / 2));
        rect.setAttribute('width', String(eye.width));
        rect.setAttribute('height', String(eye.height));
        rect.setAttribute('rx', String(Math.min(eye.width, eye.height) / 2));
        rect.setAttribute('transform', `matrix(${eye.matrix.join(' ')})`);
      });
      const time = timeMs / 1000;
      group.setAttribute(
        'transform',
        animate && mode === 'listening'
          ? `translate(${16 + Math.sin(time * 1.7) * 0.35} ${16 + Math.cos(time * 1.3) * 0.35}) rotate(${Math.sin(time) * 3}) scale(${BODY_SCALE * (1 + Math.sin(time * 2.1) * 0.035)})`
          : `translate(16 16) scale(${scale})`
      );
    };

    if (!running || mode === 'idle') {
      if (changed) paint(still, 0, false);
      return;
    }

    const animation = animationState.current ?? {
      engine: new SquishEngine('IMAI', {
        shape: mode === 'speaking' ? 'circle' : 'square',
        expression: mode === 'speaking' ? 'excited' : 'attentive',
      }),
      morphAt: 0,
    };
    animationState.current = animation;
    const { engine } = animation;
    const started = performance.now();
    const resumedAt = elapsedTime.current;
    let frame = 0;
    const draw = (now: number) => {
      const timeMs = resumedAt + now - started;
      elapsedTime.current = timeMs;
      const time = timeMs / 1000;
      if (mode === 'listening') {
        const step = getListeningFrame(timeMs);
        if (step.morphAt !== animation.morphAt) {
          animation.morphAt = step.morphAt;
          engine.setShape(
            ACTIVITY_SHAPE_SEQUENCE[step.index]!.shape,
            step.morphAt / 1000,
            ACTIVITY_SHAPE_MORPH_MS / 1000
          );
          engine.setExpression(
            EXPRESSIONS[step.index % EXPRESSIONS.length]!,
            step.morphAt / 1000,
            0.28
          );
        }
      }
      // Speaking only animates fitted eyes. Its circular body never enters the morph sequence.
      paint(
        engine.layout(time, (fitted) => movingEyes(fitted, time)),
        timeMs,
        true
      );
      frame = requestAnimationFrame(draw);
    };
    draw(started);
    return () => cancelAnimationFrame(frame);
  }, [mode, running, scale, still]);

  return (
    <svg
      viewBox="0 0 32 32"
      className="size-16 overflow-visible"
      data-voice-avatar-mode={mode}
      aria-hidden="true"
    >
      <defs>
        <linearGradient
          id={gradientId}
          ref={gradientRef}
          x1="0"
          y1="0"
          x2="1"
          y2="1"
        >
          <stop
            offset="0%"
            stopColor={voiceColorCss(voiceGradientColor(initialColor, -1))}
          />
          <stop offset="50%" stopColor={voiceColorCss(initialColor)} />
          <stop
            offset="100%"
            stopColor={voiceColorCss(voiceGradientColor(initialColor, 1))}
          />
        </linearGradient>
      </defs>
      <g ref={groupRef} transform={`translate(16 16) scale(${scale})`}>
        <path
          ref={pathRef}
          data-agent-body="true"
          d={still.body}
          fill={`url(#${gradientId})`}
          stroke={`url(#${gradientId})`}
          strokeWidth={1.5 / scale}
        />
        <g
          ref={eyesRef}
          data-agent-animated-eyes="true"
          fill={initialColor[0] < 0.58 ? '#fff' : '#17171a'}
        >
          {still.eyes.map((eye, index) => (
            <rect
              key={index}
              x={-eye.width / 2}
              y={-eye.height / 2}
              width={eye.width}
              height={eye.height}
              rx={Math.min(eye.width, eye.height) / 2}
              transform={`matrix(${eye.matrix.join(' ')})`}
            />
          ))}
        </g>
      </g>
    </svg>
  );
}
